import { Box, Button, HStack, Input, Spinner, Stack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { adminGetMedicinesByEntryLogs } from "../../../api";
import AdminOnly from "../../../components/Auth/AdminOnly";
import MyIcon from "../../../components/Icon/MyIcon";
import { colors } from "../../../styles";
import { IAdminMedicineByEntryLogsList } from "../../../types";
import MedicineModal from "./MedicineModal";

export default function EntryLogs() {

    /* pagination */

    const [page, setPage] = useState(1);
    const size = 100;

    /* form */

    interface ISearchForm {
        searchKeyword: string;
    }

    const { register, handleSubmit } = useForm<ISearchForm>({
        mode: "onChange",
    });

    const [searchKeyword, setSearchKeyword] = useState<string | null>(null);

    const onSubmit = ({ searchKeyword }: ISearchForm) => {
        setPage(1);
        setSearchKeyword(searchKeyword);
    }

    /* query */

    const { data: entryLogListData, isLoading: isEntryLogListDataLoading } = useQuery<IAdminMedicineByEntryLogsList>({
        queryKey: ['adminGetMedicinesByEntryLogs', page, size, searchKeyword],
        queryFn: () => adminGetMedicinesByEntryLogs({ page, size, searchKeyword }),
    });

    const totalPages = useMemo(() => entryLogListData?.totalPages ?? 0, [entryLogListData]);
    const currentPage = useMemo(() => entryLogListData?.currentPage ?? 0, [entryLogListData]);

    /* pagination */

    const pages = useMemo(() => {
        const startPage = Math.max(page - 4, 1);
        const endPage = Math.min(page + 5, totalPages);
        return Array.from({ length: Math.max(0, endPage - startPage + 1) }, (_, idx) => startPage + idx);
    }, [page, totalPages]);

    /* columns */

    const { isOpen: isOpenMedicineModal, onOpen: onOpenMedicineModal, onClose: onCloseMedicineModal } = useDisclosure();
    const [activeMedicineId, setActiveMedicineId] = useState<string | null>(null);

    const columns = [
        { key: "index", label: "연번" },
        { key: "nameKor", label: "이름" },
        { key: "packageUnit", label: "포장 단위" },
        { key: "all", label: "진입 수" },
        { key: "search_list", label: "by 검색 목록" },
        { key: "similar_identical", label: "by 동일 성분" },
        { key: "similar_recommend", label: "by 유사 추천" },
        { key: "keyword_list", label: "by 키워드 목록" },
    ]

    /* render */

    return (
        <AdminOnly>
            <Stack width={"100%"} padding={10}>
                <HStack justifyContent={"space-between"} alignItems={"center"} gap={10}>
                    <HStack justifyContent={"flex-start"} alignItems={"center"} gap={3}>
                        <Text fontSize={28} fontWeight={700} color={colors.black}>진입 로그</Text>
                        <Text fontSize={28} fontWeight={400} color={colors.gray}>{entryLogListData?.totalCount}</Text>
                    </HStack>
                    <HStack gap={3}>
                    </HStack>
                </HStack>
                <HStack as="form" onSubmit={handleSubmit(onSubmit)} marginY={10}>
                    <Input 
                        {...register("searchKeyword", {required:false})}
                        type="text" 
                        placeholder="Search" 
                        width={"50%"} 
                    />
                    <Box as="button" type="submit">
                        <MyIcon src={"/svg/search.svg"} alt="search" width={24} height={24} />
                    </Box>
                </HStack>

                {/* table */}

                {!isEntryLogListDataLoading ? (
                    <TableContainer marginTop={10}>
                        <Table variant='simple' scrollBehavior="smooth">
                            <Thead backgroundColor={colors.g5}>
                                <Tr>
                                    {columns.map((column) => (  
                                        <Th key={column.key}>{column.label}</Th>
                                    ))}
                                </Tr>
                            </Thead>
                            <Tbody>
                                {entryLogListData?.medicines.map((medicine, index) => (
                                    <Tr key={medicine.id}>
                                        {columns.map((column) => (
                                            <Td key={column.key}>
                                                {column.key === "index" ? String(index + 1)
                                                    : column.key === "nameKor" ? (
                                                        <Button
                                                            size={"md"}
                                                            variant={"outline"}
                                                            borderColor={colors.blue}
                                                            onClick={() => {
                                                                setActiveMedicineId(medicine.id);
                                                                onOpenMedicineModal();
                                                            }}
                                                        >
                                                            {medicine.nameKor}
                                                        </Button>
                                                    ) : column.key === "packageUnit" ? (medicine.packageUnit || "-")
                                                    : String(medicine.count[column.key as keyof typeof medicine.count] ?? "-")}
                                            </Td>
                                        ))}
                                    </Tr>
                                ))}
                            </Tbody>
                        </Table>
                    </TableContainer>
                ) : (
                    <Spinner color={colors.blue} />
                )}

                {activeMedicineId && (
                    <MedicineModal
                        isOpen={isOpenMedicineModal}
                        onClose={() => {
                            onCloseMedicineModal();
                            setActiveMedicineId(null);
                        }}
                        medicineId={activeMedicineId}
                    />
                )}

                {/* pagination */}

                <HStack justifyContent={"center"} marginTop={5} >
                    {page > 1 && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page - 1)}>Previous</Button>
                    )}
            
                    {page > 5 && (
                        <>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(1)}>1</Button>
                            <Text>...</Text>
                        </>
                    )}
            
                    {pages.map((pageNumber) => (
                        <Button
                            key={pageNumber}
                            size={"xs"} variant={"ghost"}
                            onClick={() => setPage(pageNumber)}
                            backgroundColor={pageNumber === currentPage ? colors.blue : ""}
                            color={pageNumber === currentPage ? colors.white : ""}
                        >
                            {pageNumber}
                        </Button>
                    ))}
            
                    {page < totalPages - 4 && (
                        <>
                            <Text>...</Text>
                            <Button size={"xs"} variant={"ghost"} onClick={() => setPage(totalPages)}>{totalPages}</Button>
                        </>
                    )}
            
                    {page < totalPages && (
                        <Button size={"xs"} variant={"ghost"} onClick={() => setPage(page + 1)}>Next</Button>
                    )}
                </HStack>
            </Stack>
        </AdminOnly>
    )
}
